import { Link } from "react-router-dom";
import { Palette, Clock, ArrowLeft, Check, BookOpen, Upload, DollarSign, Settings, BarChart3, ExternalLink } from "lucide-react";
import { Button } from "../../components/ui/button";

const ArticleAmazonKDPPublishing2026 = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "How to Publish a Coloring Book on Amazon KDP [2026 Guide]",
        "description": "Amazon Kindle Direct Publishing (KDP) is the easiest way to turn your coloring pages into a published book. No upfront costs, worldwide distribution, and up to 60% royalties.",
        "image": "https://colorwonder.app/og-image.png",
        "author": { "@type": "Organization", "name": "ColorWonder" },
        "publisher": {
          "@type": "Organization",
          "name": "ColorWonder",
          "logo": { "@type": "ImageObject", "url": "https://colorwonder.app/logo192.png" }
        },
        "datePublished": "2025-12-26",
        "dateModified": "2025-12-26"
      },
      {
        "@type": "HowTo",
        "name": "How to Publish a Coloring Book on Amazon KDP",
        "description": "Step-by-step guide to publishing your coloring book on Amazon KDP.",
        "totalTime": "PT3D",
        "step": [
          { "@type": "HowToStep", "name": "Create KDP Account", "text": "Go to kdp.amazon.com and sign up with your Amazon account. Complete tax interview and add payment method." },
          { "@type": "HowToStep", "name": "Prepare Your Content", "text": "Create 24-50 coloring pages using ColorWonder. Add title page, copyright page, and compile into PDF." },
          { "@type": "HowToStep", "name": "Create New Paperback", "text": "From KDP Bookshelf, click Create > Paperback. Enter book details, title, description, and keywords." },
          { "@type": "HowToStep", "name": "Upload Interior and Cover", "text": "Upload your interior PDF. Create or upload cover using KDP Cover Creator or your own design." },
          { "@type": "HowToStep", "name": "Set Pricing and Publish", "text": "Set your list price ($7.99-12.99 recommended). Submit for review. Book goes live in 24-72 hours." }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How long does it take to get approved on KDP?",
            "acceptedAnswer": { "@type": "Answer", "text": "Account setup is instant. Book review typically takes 24-72 hours. Once approved, your book is live on Amazon immediately." }
          },
          {
            "@type": "Question",
            "name": "Do I need my own ISBN?",
            "acceptedAnswer": { "@type": "Answer", "text": "No! Amazon provides a free ISBN for KDP books. This is recommended for most publishers." }
          },
          {
            "@type": "Question",
            "name": "Can I update my book after publishing?",
            "acceptedAnswer": { "@type": "Answer", "text": "Yes, you can update your interior, cover, description, and price at any time. Updates typically go live within 24-48 hours." }
          },
          {
            "@type": "Question",
            "name": "When do I get paid?",
            "acceptedAnswer": { "@type": "Answer", "text": "KDP pays royalties approximately 60 days after the end of the month in which sales were made." }
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-hero-gradient">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/blog" className="text-slate-600 hover:text-slate-900 font-medium">
                Blog
              </Link>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
                data-testid="nav-try-free-btn"
              >
                Try Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8" data-testid="back-to-blog-link">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">Publishing Guide</span>
              <span>•</span>
              <span>Dec 2025</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                14 min read
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              How to Publish a Coloring Book on Amazon KDP [2026 Guide]
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              Amazon Kindle Direct Publishing (KDP) is the easiest way to turn your coloring pages into a published book. No upfront costs, worldwide distribution, and up to 60% royalties. Here's your complete step-by-step guide.
            </p>

            <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-100 flex items-start gap-3">
              <BookOpen className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800">
                <strong>By the end of this guide:</strong> You'll know exactly how to take your coloring pages from ColorWonder and publish them as a real book on Amazon - available worldwide.
              </p>
            </div>
          </header>

          {/* Table of Contents */}
          <div className="bg-slate-50 rounded-2xl p-6 mb-12" data-testid="table-of-contents">
            <h2 className="font-semibold text-slate-800 mb-4">📋 Table of Contents</h2>
            <ul className="space-y-2 text-slate-600">
              <li><a href="#what-is-kdp" className="hover:text-violet-600">1. What is Amazon KDP?</a></li>
              <li><a href="#requirements" className="hover:text-violet-600">2. Requirements & Formatting Specs</a></li>
              <li><a href="#create-content" className="hover:text-violet-600">3. Creating Your Coloring Book Content</a></li>
              <li><a href="#setup-account" className="hover:text-violet-600">4. Setting Up Your KDP Account</a></li>
              <li><a href="#upload-book" className="hover:text-violet-600">5. Step-by-Step: Uploading Your Book</a></li>
              <li><a href="#cover-design" className="hover:text-violet-600">6. Designing Your Book Cover</a></li>
              <li><a href="#pricing" className="hover:text-violet-600">7. Pricing for Maximum Profit</a></li>
              <li><a href="#launch" className="hover:text-violet-600">8. Launch & Marketing Checklist</a></li>
              <li><a href="#faq" className="hover:text-violet-600">9. Frequently Asked Questions</a></li>
            </ul>
          </div>

          {/* Section 1: What is KDP */}
          <section id="what-is-kdp" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              1. What is Amazon KDP?
            </h2>
            
            <p className="text-slate-600 mb-6">
              Amazon Kindle Direct Publishing (KDP) is Amazon's self-publishing platform that lets anyone publish and sell books on the world's largest bookstore - completely free.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {[
                { title: "Print-on-Demand", desc: "Books are only printed when someone orders. No inventory, no upfront costs.", icon: "📦" },
                { title: "Global Reach", desc: "Your book available in 13 Amazon marketplaces worldwide instantly.", icon: "🌍" },
                { title: "High Royalties", desc: "Earn up to 60% royalties on paperback books (better than traditional publishing).", icon: "💰" },
                { title: "Free to Use", desc: "No fees to upload or publish. You only pay production costs when you sell.", icon: "✨" }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">{item.title}</h4>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-green-50 rounded-xl p-5 border border-green-100">
              <p className="text-green-800">
                <strong>Perfect for coloring books:</strong> KDP's print-on-demand model means you can test multiple niche coloring books without any financial risk. No inventory to manage, no minimum orders.
              </p>
            </div>
          </section>

          {/* Section 2: Requirements */}
          <section id="requirements" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              2. Requirements & Formatting Specs
            </h2>
            
            <p className="text-slate-600 mb-6">
              Before uploading, your coloring book needs to meet Amazon's formatting requirements. Here are the key specifications:
            </p>

            <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-6">
              <h4 className="font-semibold text-slate-800 mb-4">📐 Interior Requirements</h4>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { spec: "File Format", value: "PDF (required)" },
                  { spec: "Resolution", value: "300 DPI minimum" },
                  { spec: "Popular Size", value: "8.5\" x 11\" (letter)" },
                  { spec: "Min Pages", value: "24 pages" },
                  { spec: "Max Pages", value: "800 pages" },
                  { spec: "Color Mode", value: "Grayscale or B&W" },
                  { spec: "Margins", value: "0.25\" minimum" },
                  { spec: "Bleed", value: "0.125\" if images touch edge" }
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                    <span className="text-slate-600">{item.spec}</span>
                    <span className="font-medium text-slate-800">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-6">
              <h4 className="font-semibold text-slate-800 mb-4">📕 Cover Requirements</h4>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { spec: "File Format", value: "PDF or JPEG" },
                  { spec: "Resolution", value: "300 DPI minimum" },
                  { spec: "Color Mode", value: "RGB" },
                  { spec: "Bleed", value: "0.125\" on all sides" }
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                    <span className="text-slate-600">{item.spec}</span>
                    <span className="font-medium text-slate-800">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-violet-50 rounded-xl p-5 border border-violet-100">
              <h4 className="font-semibold text-violet-800 mb-2 flex items-center gap-2">
                <Check className="w-5 h-5" />
                ColorWonder Handles Most of This
              </h4>
              <p className="text-violet-700 text-sm">
                All pages generated with ColorWonder are automatically 300 DPI, proper sizing, clean black and white lines. You just need to compile them into a PDF and add front/back matter.
              </p>
            </div>
          </section>

          {/* Section 3: Creating Content */}
          <section id="create-content" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              3. Creating Your Coloring Book Content
            </h2>
            
            <p className="text-slate-600 mb-6">
              A successful coloring book needs more than just coloring pages. Here's what to include:
            </p>

            <div className="space-y-4 mb-6">
              {[
                { page: "Title Page", desc: "Book title, subtitle, author name", required: true },
                { page: "Copyright Page", desc: "© Year, Your Name. All rights reserved.", required: true },
                { page: "Dedication/Intro", desc: "Optional personal message to readers", required: false },
                { page: "Coloring Pages", desc: "Your main content - 24-50 pages typical", required: true },
                { page: "Blank Back Pages", desc: "One-sided printing prevents bleed-through", required: true },
                { page: "\"About\" Page", desc: "Brief bio and links to more books", required: false }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-100">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${item.required ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}>
                    {item.required ? <Check className="w-4 h-4" /> : <span className="text-xs">opt</span>}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-800">{item.page}</div>
                    <div className="text-sm text-slate-500">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <h4 className="font-semibold text-slate-800 mb-4">Creating Pages with ColorWonder:</h4>
            <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-2xl p-6 border border-violet-100">
              <ol className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-violet-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                  <div>
                    <div className="font-medium text-slate-800">Choose Your Theme</div>
                    <div className="text-sm text-slate-600">Pick a specific niche: "Cute dinosaurs", "Mandala patterns", "Farm animals"</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-violet-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                  <div>
                    <div className="font-medium text-slate-800">Generate 30-50 Pages</div>
                    <div className="text-sm text-slate-600">Use varied prompts within your theme for variety</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-violet-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                  <div>
                    <div className="font-medium text-slate-800">Download & Compile</div>
                    <div className="text-sm text-slate-600">Combine all pages into a single PDF with title pages</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-violet-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                  <div>
                    <div className="font-medium text-slate-800">Add Blank Backs</div>
                    <div className="text-sm text-slate-600">Insert blank pages after each coloring page for single-sided printing</div>
                  </div>
                </li>
              </ol>
              
              <Button
                onClick={() => window.location.href = "/signup"}
                className="mt-6 h-11 px-6 rounded-full bg-violet-600 text-white font-medium hover:bg-violet-700"
                data-testid="cta-create-pages-btn"
              >
                Start Creating Pages →
              </Button>
            </div>
          </section>

          {/* Section 4: Setup Account */}
          <section id="setup-account" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              4. Setting Up Your KDP Account
            </h2>
            
            <p className="text-slate-600 mb-6">
              Creating a KDP account is free and takes about 10 minutes. Here's what you'll need:
            </p>

            <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-6">
              <h4 className="font-semibold text-slate-800 mb-4">📝 Information Required:</h4>
              <ul className="space-y-3">
                {[
                  "Email address (create a new one for publishing if you prefer)",
                  "Tax information (SSN for US, tax ID for international)",
                  "Bank account for royalty payments",
                  "Address for tax purposes"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-600">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <span className="w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold flex-shrink-0">1</span>
                <div>
                  <div className="font-medium text-slate-800">Go to kdp.amazon.com</div>
                  <div className="text-sm text-slate-500">Click "Sign up" or use existing Amazon account</div>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <span className="w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold flex-shrink-0">2</span>
                <div>
                  <div className="font-medium text-slate-800">Complete Tax Interview</div>
                  <div className="text-sm text-slate-500">Answer questions about your tax status (required for payment)</div>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <span className="w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold flex-shrink-0">3</span>
                <div>
                  <div className="font-medium text-slate-800">Add Payment Method</div>
                  <div className="text-sm text-slate-500">Enter bank account details for royalty deposits</div>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <span className="w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold flex-shrink-0">4</span>
                <div>
                  <div className="font-medium text-slate-800">You're Ready!</div>
                  <div className="text-sm text-slate-500">Account approved instantly - start uploading books</div>
                </div>
              </div>
            </div>

            <a 
              href="https://kdp.amazon.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-violet-600 font-medium hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              Create your KDP account →
            </a>
          </section>

          {/* Section 5: Upload Book */}
          <section id="upload-book" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              5. Step-by-Step: Uploading Your Book
            </h2>
            
            <p className="text-slate-600 mb-6">
              Once your account is set up and your PDF is ready, here's exactly how to publish:
            </p>

            <div className="space-y-6 mb-6">
              {/* Step 1 */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold">1</span>
                  <h4 className="font-bold text-slate-800">Create New Paperback</h4>
                </div>
                <p className="text-slate-600 mb-4">From your KDP Bookshelf, click "Create" → "Paperback"</p>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600">💡 Choose "Paperback" not "Hardcover" - it's more affordable for coloring books and what buyers expect.</p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold">2</span>
                  <h4 className="font-bold text-slate-800">Enter Book Details</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Settings className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-slate-800 text-sm">Title</div>
                      <div className="text-xs text-slate-500">Include keywords: "Cute Dinosaur Coloring Book for Kids Ages 4-8"</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Settings className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-slate-800 text-sm">Author Name</div>
                      <div className="text-xs text-slate-500">Your name or pen name</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Settings className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-slate-800 text-sm">Description</div>
                      <div className="text-xs text-slate-500">2-3 paragraphs with keywords, benefits, and who it's for</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Settings className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-slate-800 text-sm">Categories</div>
                      <div className="text-xs text-slate-500">Choose 2 categories that match your book (coloring books, kids activities)</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Settings className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-slate-800 text-sm">Keywords</div>
                      <div className="text-xs text-slate-500">7 keyword phrases (e.g., "dinosaur coloring book", "kids coloring pages")</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold">3</span>
                  <h4 className="font-bold text-slate-800">Upload Your Content</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Upload className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-slate-800 text-sm">Print ISBN</div>
                      <div className="text-xs text-slate-500">Get FREE ISBN from Amazon (recommended) or use your own</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Upload className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-slate-800 text-sm">Interior Settings</div>
                      <div className="text-xs text-slate-500">Choose: Black & white interior, White paper, No bleed (unless pages touch edge)</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Upload className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-slate-800 text-sm">Manuscript</div>
                      <div className="text-xs text-slate-500">Upload your interior PDF file</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Upload className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-slate-800 text-sm">Cover</div>
                      <div className="text-xs text-slate-500">Upload cover PDF/JPEG or use Cover Creator tool</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold">4</span>
                  <h4 className="font-bold text-slate-800">Preview & Publish</h4>
                </div>
                <p className="text-slate-600 mb-3">Use the online previewer to check:</p>
                <ul className="space-y-2 text-slate-600 mb-4">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> All pages display correctly</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> No content cut off at edges</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Cover looks professional</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Spine text (if any) is readable</li>
                </ul>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-800">✅ Once approved (usually 24-72 hours), your book will be live on Amazon worldwide!</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6: Cover Design */}
          <section id="cover-design" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              6. Designing Your Book Cover
            </h2>
            
            <p className="text-slate-600 mb-6">
              Your cover is the #1 factor in whether someone clicks on your book. Here's what makes coloring book covers sell:
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {[
                { do: true, tip: "Show sample pages on front cover" },
                { do: true, tip: "Use bright, eye-catching colors" },
                { do: true, tip: "Large, readable title text" },
                { do: true, tip: "Include age range if for kids" },
                { do: false, tip: "Cluttered design with too much text" },
                { do: false, tip: "Generic stock photos" },
                { do: false, tip: "Small fonts that can't be read in thumbnail" },
                { do: false, tip: "Similar look to competitors" }
              ].map((item, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-lg ${item.do ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                  {item.do ? (
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <span className="w-5 h-5 flex items-center justify-center text-red-600 font-bold">✕</span>
                  )}
                  <span className={item.do ? 'text-green-800' : 'text-red-800'}>{item.tip}</span>
                </div>
              ))}
            </div>

            <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
              <h4 className="font-semibold text-amber-800 mb-2">🎨 Cover Creation Options:</h4>
              <ul className="space-y-2 text-amber-700">
                <li><strong>Free:</strong> Use Amazon's built-in Cover Creator tool</li>
                <li><strong>DIY:</strong> Canva has free coloring book cover templates</li>
                <li><strong>Professional:</strong> Hire on Fiverr ($20-50 for quality cover)</li>
              </ul>
            </div>
          </section>

          {/* Section 7: Pricing */}
          <section id="pricing" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              7. Pricing for Maximum Profit
            </h2>
            
            <p className="text-slate-600 mb-6">
              Amazon KDP offers 60% royalties on paperback books. Here's how to price for profit:
            </p>

            <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-6">
              <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Royalty Calculator Example
              </h4>
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <div className="text-sm text-slate-500 mb-2">For a 50-page coloring book (8.5x11", B&W interior):</div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">List Price</span>
                    <span className="font-medium text-slate-800">$9.99</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Printing Cost</span>
                    <span className="text-slate-800">-$3.85</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-2">
                    <span className="font-semibold text-slate-800">Your Royalty</span>
                    <span className="font-bold text-green-600">$2.29 per sale</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-slate-500">* Actual printing costs vary based on page count and marketplace</p>
            </div>

            <h4 className="font-semibold text-slate-800 mb-4">Recommended Price Points:</h4>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {[
                { pages: "24-30 pages", price: "$6.99-7.99", royalty: "~$1.50-2.00" },
                { pages: "40-50 pages", price: "$8.99-9.99", royalty: "~$2.00-2.50" },
                { pages: "60-80 pages", price: "$10.99-12.99", royalty: "~$2.50-3.50" }
              ].map((tier, i) => (
                <div key={i} className="bg-violet-50 rounded-xl p-4 border border-violet-100 text-center">
                  <div className="text-sm text-violet-600 font-medium">{tier.pages}</div>
                  <div className="text-xl font-bold text-slate-800 my-1">{tier.price}</div>
                  <div className="text-sm text-slate-600">Royalty: {tier.royalty}</div>
                </div>
              ))}
            </div>

            <div className="bg-green-50 rounded-xl p-5 border border-green-100">
              <p className="text-green-800">
                <strong>💡 Pro Tip:</strong> Start at a competitive price to get initial sales and reviews, then gradually increase as your book gains traction.
              </p>
            </div>
          </section>

          {/* Section 8: Launch */}
          <section id="launch" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              8. Launch & Marketing Checklist
            </h2>
            
            <p className="text-slate-600 mb-6">
              Your book is live! Here's how to drive those first crucial sales:
            </p>

            <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-6">
              <h4 className="font-semibold text-slate-800 mb-4">📋 Week 1 Launch Checklist:</h4>
              <div className="space-y-3">
                {[
                  "Share on your social media accounts",
                  "Post sample pages on Pinterest (5-10 pins)",
                  "Create Instagram post with cover image",
                  "Ask friends/family to purchase and review",
                  "Join relevant Facebook groups, share value (not spam)",
                  "Consider running Amazon Ads ($5/day to start)"
                ].map((task, i) => (
                  <label key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                    <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                    <span className="text-slate-700">{task}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="bg-violet-50 rounded-xl p-5 border border-violet-100">
              <h4 className="font-semibold text-violet-800 mb-2 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Expected Timeline:
              </h4>
              <ul className="space-y-1 text-violet-700 text-sm">
                <li>• <strong>Week 1-2:</strong> First 5-10 sales from your network</li>
                <li>• <strong>Month 1:</strong> Organic Amazon sales begin</li>
                <li>• <strong>Month 2-3:</strong> Reviews accumulate, rankings improve</li>
                <li>• <strong>Month 6+:</strong> Steady passive income if marketed well</li>
              </ul>
            </div>
          </section>

          {/* Final CTA */}
          <section className="mb-12">
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Ready to Create Your KDP Coloring Book?
              </h3>
              <p className="text-violet-100 mb-6 max-w-md mx-auto">
                Start with ColorWonder to generate your coloring pages, then follow this guide to publish on Amazon. Your book could be live within a week!
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  onClick={() => window.location.href = "/signup"}
                  className="h-14 px-8 rounded-full bg-white text-violet-600 font-semibold text-lg hover:bg-violet-50"
                  data-testid="final-cta-btn"
                >
                  Start Creating Pages →
                </Button>
              </div>
              <p className="text-violet-200 text-sm mt-4">10 free pages/month • Commercial license with Plus tier</p>
            </div>
          </section>

          {/* FAQ Section */}
          <section id="faq" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "How long does it take to get approved on KDP?",
                  a: "Account setup is instant. Book review typically takes 24-72 hours. Once approved, your book is live on Amazon immediately."
                },
                {
                  q: "Do I need my own ISBN?",
                  a: "No! Amazon provides a free ISBN for KDP books. This is recommended for most publishers as it simplifies the process."
                },
                {
                  q: "Can I update my book after publishing?",
                  a: "Yes, you can update your interior, cover, description, and price at any time. Updates typically go live within 24-48 hours."
                },
                {
                  q: "What if my book gets rejected?",
                  a: "Amazon will email you the specific issue (usually formatting). Fix the problem and resubmit - there's no penalty for rejections."
                },
                {
                  q: "Can I sell in countries other than the US?",
                  a: "Yes! KDP distributes to 13 Amazon marketplaces worldwide including UK, Germany, France, Japan, and more."
                },
                {
                  q: "When do I get paid?",
                  a: "KDP pays royalties approximately 60 days after the end of the month in which sales were made. Payments are deposited directly to your bank account."
                }
              ].map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-slate-100">
                  <h4 className="font-semibold text-slate-800 mb-2">{faq.q}</h4>
                  <p className="text-slate-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-12">
            <h3 className="font-semibold text-slate-800 mb-4">Related Articles</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/blog/how-to-make-coloring-book" className="p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 transition-colors group">
                <h4 className="font-medium text-slate-800 group-hover:text-violet-600">How to Make a Coloring Book →</h4>
                <p className="text-sm text-slate-500">Complete creation guide</p>
              </Link>
              <Link to="/blog/coloring-book-to-sell" className="p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 transition-colors group">
                <h4 className="font-medium text-slate-800 group-hover:text-violet-600">Create Coloring Books to Sell →</h4>
                <p className="text-sm text-slate-500">Full profit guide</p>
              </Link>
            </div>
          </section>

          {/* Author Bio */}
          <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <Palette className="w-8 h-8 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-800">About ColorWonder</h4>
                <p className="text-slate-600 text-sm mt-1">
                  ColorWonder helps creators publish coloring books faster than ever. Generate pages in seconds, then use this guide to publish on Amazon KDP.
                </p>
                <Link to="/pricing" className="text-violet-600 text-sm font-medium mt-2 inline-block hover:underline">
                  Get started with ColorWonder →
                </Link>
              </div>
            </div>
          </div>

        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">© 2025 ColorWonder. All rights reserved.</p>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-700">Terms</Link>
            <Link to="/contact" className="hover:text-slate-700">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ArticleAmazonKDPPublishing2026;
